<template>
  <div class="c-form-task">
    <div class="c-form-task__content">
      <div class="c-form-task__col">
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">施策内容を選択</p>
          </div>
          <div class="c-fieldset__content c-alertPos">
            <Select
              v-model="params.type"
              :options="types"
              placeholder="施策内容を選択"
              placement="top"
              :selected="params.type"
              size="sm"
              required
            />
            <Alert v-if="error.type">
              <span v-html="error.type"></span>
            </Alert>
          </div>
        </div>
      </div>
      <div class="c-form-task__col">
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">担当者を設定</p>
          </div>
          <div class="c-fieldset__content c-alertPos">
            <Select
              v-model="params.operator"
              :options="projectUsers"
              placeholder="担当者なし"
              placement="top"
              :selected="params.operator"
              size="sm"
              :class="{
                alert: error.operator
              }"
            />
            <Alert v-if="error.operator">
              {{ error.operator }}
            </Alert>
          </div>
        </div>
      </div>
      <div class="c-form-task__col">
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">詳細</p>
          </div>
          <div class="c-fieldset__content c-alertPos">
            <Textarea
              v-model="params.text"
              placeholder="タスクの詳細を記入"
              size="sm"
              :class="{
                alert: isEdited.text && error.text
              }"
            />
            <Alert v-if="isEdited.text && error.text">
              {{ error.text }}
            </Alert>
          </div>
        </div>
        <div class="c-alertPos l-mt-3">
          <Button
            variant="main"
            fsize="f14"
            icon="plus"
            block
            class="l-pr-2"
            :class="{ 'is-loading': isProcessing }"
            :disabled="isDisabled"
            @click="submit"
          >
            タスクを追加
          </Button>
          <Alert v-if="errorResponse">
            {{ errorResponse }}
          </Alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Select from '@/components/Select';
import Textarea from '@/components/Textarea';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    count: {
      type: Number,
      required: true
    },
    date: {
      type: String,
      required: false
    },
    items: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    defaultType: {
      type: String,
      required: false
    },
    page: {
      type: String,
      required: false
    },
    version: {
      type: Number,
      required: false
    }
  },
  components: {
    Alert,
    Button,
    Select,
    Textarea
  },
  data() {
    return {
      error: {
        operator: '',
        text: '',
        type: ''
      },
      errorResponse: '',
      params: {
        operator: '',
        text:
          this.defaultType == 'createContent'
            ? '新規でコンテンツを作成する'
            : '',
        type: this.defaultType ? this.defaultType : ''
      },
      usages: {
        rest: 0
      },
      isEdited: {
        operator: false,
        text: false,
        type: false
      },
      isError: false,
      isOver: false,
      isProcessing: false
    };
  },
  computed: {
    projectUsers() {
      return this.$store.getters['project/usersSelect'];
    },
    types() {
      return this.$store.getters['task/solutionSelect'];
    },
    isDisabled() {
      return !this.count || this.isError || this.isProcessing;
    }
  },
  watch: {
    count: {
      handler() {
        this.checkTypeValidation();
        this.resetError();
      }
    },
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        this.resetError();
      },
      deep: true
    },
    'params.operator': {
      handler(value) {
        const operator = value;
        this.error.operator = '';
        this.isEdited.operator = operator ? true : false;
      },
      immediate: true
    },
    'params.text': {
      handler() {
        let text = this.params.text.trim();
        this.error.text = new Validation()
          .input()
          .maxLength(TASK_LENGTHS_LIMIT)
          .test(text);
        this.isEdited.text = text ? true : false;
      },
      immediate: true
    },
    'params.type': {
      handler() {
        let type = this.params.type;
        this.isEdited.type = type ? true : false;
        this.checkTypeValidation();
      },
      immediate: true
    }
  },
  created() {
    this.$store.dispatch('project/getUsers');
    this.$store.dispatch('task/getTaskSolutions');
  },
  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    // タスクの施策タイプごとに必須のパラメータが欠如していないかチェック
    checkTypeValidation() {
      // 現状は関連ワード分析と競合キーワード分析のみチェック
      const COMPETITIVE_KEYWORD = 'competitiveKeyword';
      const COMPETITIVE_SITE_KEYWORD = 'competitiveSiteKeyword';
      const requirePage = [COMPETITIVE_KEYWORD, COMPETITIVE_SITE_KEYWORD];
      if (!requirePage.includes(this.page)) {
        return;
      }
      // URLが必須の施策タイプ
      const requireUrl = ['rewrite', 'changeTd', 'seoCheckUp'];
      if (requireUrl.includes(this.params.type)) {
        // 関連ワード分析バージョン1の場合はエラー
        if (this.page === COMPETITIVE_KEYWORD && this.version === 1) {
          this.error.type =
            'タイトル変更、リライト、SEO内部改善のタスク登録するためには、<br />お手数ですが再分析をお願いします';
          return;
        }
        const noUrlItem = this.items.find(
          item => !item.ownSiteUrl && !item.pageUrl
        );
        if (noUrlItem) {
          this.error.type =
            '自社URLが存在しないキーワードが含まれているため、選択できません';
          return;
        }
      }
      this.error.type = '';
    },
    async submit() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      // 利用量チェック
      await this.getUsages();
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }
      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await Promise.all(
        this.items.map(item => {
          return this.postTask(item);
        })
      )
        .then(response => {
          this.params.operator = '';
          this.params.text = '';
          this.params.type = '';
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });

      this.$store.dispatch('project/getTaskCount');

      // 現状は関連ワード分析のみ登録時にイベントを送信する
      const COMPETITIVE_KEYWORD = 'competitiveKeyword';
      if (this.page == COMPETITIVE_KEYWORD) {
        this.$emit('pushSubmitEvent');
      }
      this.isProcessing = false;

      this.$emit('reset');
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const usagesRest = usages.max - usages.current;
          const error = utils.getErrorUsages({
            count: this.count,
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse = error.message;
          }
          if (usagesRest > 0 && usagesRest < this.count) {
            this.usages.rest = usagesRest;
            this.isOver = true;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        const existUser = this.projectUsers.some(
          user => user.value === this.params.operator
        );
        if (!existUser) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask(item) {
      const params = {
        column: 'todo',
        keyword: null,
        operatorUserId: this.params.operator || null,
        taskDetail: this.params.text,
        type: this.params.type,
        url: null
      };

      params.keyword = item.keyword;

      // 検索順位チェックの場合はurl, 新規キーワード発見の場合ownSiteUrl、関連ワード分析の場合pageUrl
      if (this.page === 'competitiveSiteKeyword') {
        params.url = item.ownSiteUrl;
      } else if (this.page === 'competitiveKeyword') {
        params.url = item.pageUrl;
      } else {
        params.url = item.url;
      }
      // タスクの詳細欄にキーワード情報を追加
      this.setTaskDetail(params, item);

      return apiProjects.tasks.siteAnalysis.post(
        params,
        this.auth.idToken,
        this.project.id
      );
    },
    setTaskDetail(params, item) {
      let detail = params.taskDetail;
      const format = (label, value) => {
        return `\n【${label}】 ${value}`;
      };
      const NO_DATA_TEXT =
        'データ取得中にタスク登録されたためデータがありません';
      if (this.page === 'competitiveKeyword') {
        if (this.date) {
          detail += format(
            '分析データ登録日',
            utils.formatDate(this.date, 'YYYY.MM.DD')
          );
        }
        if (item.rank) {
          if (item.rank > 100) {
            detail += format('自社順位', '100位以下');
          } else {
            detail += format('自社順位', item.rank + '位');
          }
        }
        if (item.searchVolume !== null) {
          detail += format(
            '月間検索数',
            this.separateNumber(item.searchVolume)
          );
        }
      }
      if (this.page === 'competitiveSiteKeyword') {
        if (this.date) {
          detail += format('調査日', utils.formatDate(this.date, 'YYYY.MM.DD'));
        }
        if (item.searchVolume !== null) {
          detail += format(
            '月間検索数',
            this.separateNumber(item.searchVolume)
          );
        }
        if (item.competitorSiteRank) {
          detail += format('競合順位', item.competitorSiteRank + '位');
        } else {
          // このパターンは起こらないが他にはelseがあってこれにはないのはなんでだと考えてしまいそうなので記述
          detail += format('競合順位', item.competitorSiteRank + '位');
        }
        if (!item.ownSiteRank) {
          detail += format('自社平均順位 ', NO_DATA_TEXT);
        } else if (item.ownSiteRank === 9999) {
          detail += format('自社平均順位', '圏外');
        } else {
          detail += format('自社平均順位', item.ownSiteRank + '位');
        }
        if (!item.ownSiteRank) {
          detail += format('GAP', NO_DATA_TEXT);
        } else if (item.ownSiteRank !== 9999) {
          detail += format('GAP', item.rankGap);
        } else {
          detail += format('GAP', '-');
        }
        if (item.competitorSitePageUrl) {
          detail += format('競合URL', item.competitorSitePageUrl);
        } else {
          // このパターンは起こらないが他にはelseがあってこれにはないのはなんでだと考えてしまいそうなので記述
          detail += format('競合URL', '-');
        }
        if (item.ownSiteUrl) {
          detail += format('自社URL', item.ownSiteUrl);
        } else if (!item.ownSiteRank) {
          detail += format('自社URL', NO_DATA_TEXT);
        } else {
          detail += format('自社URL', '-');
        }
      }
      params.taskDetail = detail;
    },
    separateNumber(number) {
      if (number === null) {
        return number;
      }
      return String(number).replace(/^(-?\d+)(?=\.|$)/, s => {
        return s.replace(/(\d+?)(?=(?:\d{3})+$)/g, '$1,');
      });
    },
    resetError() {
      if (!this.isOver || this.usages.rest >= this.count) {
        this.errorResponse = '';
        this.isOver = false;
      }
    }
  }
};
</script>
